import { useEffect } from 'react';
import { MarketingShell } from '@/components/marketing-shell';
import { buttonVariants } from '@okrapdf/ui';
import { cn } from '@/lib/utils';
import { brandCopy, getBrand, setPageSeo } from '@/lib/brand';

export function DurableDocumentLayerPage() {
  useEffect(() => {
    const brand = getBrand();
    setPageSeo(
      {
        title: 'The Durable Document Layer | okraPDF',
        description:
          'Your document is a server, not a file. Parse once — durably. Every page resumable, every run reused by every agent. The durable substrate under your document AI, on your own infrastructure.',
      },
      brand,
    );

    let ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = brandCopy('The Durable Document Layer | okraPDF', brand);
  }, []);

  return (
    <MarketingShell>
      <main className="bg-background text-foreground">
        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <div className="max-w-3xl">
              <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 font-mono text-[11px] font-medium text-primary shadow-okra-xs">
                durable-document-layer
              </span>
              <h1 className="mt-6 font-serif text-[clamp(36px,6vw,62px)] font-semibold leading-[1.04] tracking-[-0.025em]">
                The Durable Document Layer
              </h1>
              <p className="mt-5 max-w-[58ch] text-[17px] leading-relaxed text-muted-foreground">
                Your document is a server, not a file. Parse once — durably. Every page resumable, every run reused by every agent. The durable substrate under your document AI, on your own infrastructure.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="/docs/okra-solo"
                  className={cn(buttonVariants({ variant: 'default', size: 'lg' }), 'shadow-sm')}
                >
                  Deploy okra-solo
                </a>
                <a
                  href="https://docs.okrapdf.com"
                  className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'shadow-okra-xs')}
                >
                  Read the docs
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted">
          <div className="mx-auto grid max-w-5xl gap-10 px-6 py-14 lg:grid-cols-2">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-primary">
                The Gap
              </div>
              <h2 className="mt-2 font-serif text-[30px] font-semibold tracking-[-0.02em]">
                Why current pipelines fail
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                Call an OCR API or run a local script and you get no durability: crash at page 150 of 200, start from zero. Self-host a model and the docs say it plainly — <em>you&apos;re now responsible for the workflow tooling around the core model.</em> Retries, resume, state, idempotency: all yours to build.
              </p>
            </div>
            
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-primary">
                Out of the box
              </div>
              <h2 className="mt-2 font-serif text-[30px] font-semibold tracking-[-0.02em]">
                Durable workflow
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                okra runs every document as its own Durable Object on Cloudflare. The parse is durable, resumable, and at-least-once per page — an 80-minute, 376-page run survives a restart and picks up where it stopped. No Temporal, no queue, no state store to wire.
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-10 px-6 py-14 lg:grid-cols-2">
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-primary">
                Architecture
              </div>
              <h2 className="mt-2 font-serif text-[30px] font-semibold tracking-[-0.02em]">
                A document is durable; an agent is transient.
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                Your agent framework makes the <em>agent</em> durable. okra makes the <em>document</em> durable — a long-lived server, parsed once and attached to by many agents over time, outliving any single run. That&apos;s why okra drops into Flue / Claude Agent SDK / LangChain instead of competing with them: a different durable primitive.
              </p>
            </div>
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-[0.04em] text-primary">
                Plugins
              </div>
              <h2 className="mt-2 font-serif text-[30px] font-semibold tracking-[-0.02em]">
                Compose the parsers. Control the egress.
              </h2>
              <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
                Swap parsers as model-named plugins — Gemini, Qwen-VL, Mistral OCR, your own Docling — each pinned to the hosts it&apos;s allowed to reach, on your own Cloudflare account. Want it fully private? Pick a no-egress parser and the document never leaves.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-muted">
          <div className="mx-auto max-w-5xl px-6 py-14 text-center">
            <h2 className="font-serif text-[30px] font-semibold tracking-[-0.02em]">
              Bring your own keys. Deploy in one click.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-[14px] leading-relaxed text-muted-foreground">
              okra-solo is a single Cloudflare Worker you own — <code>wrangler deploy</code> or the deploy button. Your account, your keys, your documents.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <a
                href="/docs/okra-solo"
                className={cn(buttonVariants({ variant: 'default', size: 'lg' }), 'shadow-sm')}
              >
                Deploy okra-solo
              </a>
            </div>
          </div>
        </section>
      </main>
    </MarketingShell>
  );
}
